import type { HTMLAttributes, ReactNode } from "react";
import { CheckboxBase } from "@/components/base/checkbox/checkbox";
import { cx } from "@/utils/cx";

export const TableSelectionCellHeaderState = {
	Unchecked: "unchecked",
	Checked: "checked",
	Indeterminate: "indeterminate",
} as const;

export type TableSelectionCellHeaderState =
	(typeof TableSelectionCellHeaderState)[keyof typeof TableSelectionCellHeaderState];

export interface TableSelectionCellHeaderProps
	extends Omit<HTMLAttributes<HTMLDivElement>, "children"> {
	/**
	 * Select-all checkbox state (Figma `state`).
	 * `indeterminate` when some — but not all — rows are selected.
	 * Ignored when `children` provides a live React Aria checkbox.
	 */
	state?: TableSelectionCellHeaderState;
	/**
	 * Compose a live checkbox (e.g. React Aria `Checkbox slot="selection"`).
	 * Defaults to a presentational {@link CheckboxBase} for Storybook.
	 */
	children?: ReactNode;
}

/**
 * Selection header chrome matching Figma Table/SelectionCellHeader (3045:125).
 * Anchors the leading column of the header row for select-all.
 */
export function TableSelectionCellHeader({
	state = TableSelectionCellHeaderState.Unchecked,
	className,
	children,
	...props
}: TableSelectionCellHeaderProps) {
	const isIndeterminate = state === TableSelectionCellHeaderState.Indeterminate;
	const isSelected =
		state === TableSelectionCellHeaderState.Checked || isIndeterminate;

	return (
		<div
			{...props}
			data-state={state}
			className={cx(
				// Figma Table/SelectionCellHeader — h-40, pl-32 pr-4 py-12, bg-tertiary
				"relative flex h-10 w-[52px] items-center bg-tertiary py-3 pr-1 pl-8",
				className,
			)}
		>
			{children ?? (
				<CheckboxBase
					size="sm"
					isSelected={isSelected}
					isIndeterminate={isIndeterminate}
				/>
			)}
		</div>
	);
}
